import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Toaster } from '@/components/ui/sonner';
import {
  ClipboardCheck, Car, UserCheck, Wrench, Loader2, Shield, AlertTriangle,
  CheckCircle2, Upload,
} from 'lucide-react';

const AUDIT_TYPE_META = {
  Allotment:  { icon: UserCheck,       color: 'bg-blue-50 text-blue-700 border-blue-200' },
  Submission: { icon: ClipboardCheck,  color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  Repairs:    { icon: Wrench,          color: 'bg-rose-50 text-rose-700 border-rose-200' },
  Parking:    { icon: Car,             color: 'bg-amber-50 text-amber-700 border-amber-200' },
};

const DEFAULT_INVENTORY = {
  tyre1_brand: '', tyre1_number: '',
  tyre2_brand: '', tyre2_number: '',
  tyre3_brand: '', tyre3_number: '',
  tyre4_brand: '', tyre4_number: '',
  stepney_brand: '', stepney_number: '',
  battery_brand: '', battery_number: '',
  tool_kit_details: '',
};

const PHOTO_FIELDS = [
  { key: 'car_side_1',      label: 'Car Side 1' },
  { key: 'car_side_2',      label: 'Car Side 2' },
  { key: 'car_side_3',      label: 'Car Side 3' },
  { key: 'car_side_4',      label: 'Car Side 4' },
  { key: 'driver_with_car', label: 'Driver with Car' },
];

const toBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

export default function AuditLinkPage() {
  const { token } = useParams();
  const API = `${process.env.REACT_APP_BACKEND_URL}/api/audit-links/public/${token}`;

  const [status, setStatus] = useState('loading');  // loading | valid | invalid
  const [link, setLink] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);

  const [auditType, setAuditType] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [driverId, setDriverId] = useState('');
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [reason, setReason] = useState('');
  const [inventory, setInventory] = useState(DEFAULT_INVENTORY);
  const [photos, setPhotos] = useState({});   // key -> File
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastSubmitted, setLastSubmitted] = useState(null);

  const bootstrap = useCallback(async () => {
    try {
      const [v, vs, ds] = await Promise.all([
        axios.get(`${API}/verify`),
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/drivers`),
      ]);
      setLink(v.data);
      setVehicles(vs.data || []);
      setDrivers(ds.data || []);
      setStatus('valid');
    } catch (e) {
      setStatus('invalid');
    }
  }, [API]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const filteredVehicles = useMemo(() => {
    const q = vehicleSearch.trim().toLowerCase();
    if (!q) return vehicles;
    return vehicles.filter(v =>
      (v.vehicle_number || '').toLowerCase().includes(q) ||
      (v.make || '').toLowerCase().includes(q) ||
      (v.model || '').toLowerCase().includes(q)
    );
  }, [vehicles, vehicleSearch]);

  const resetForm = () => {
    setAuditType('');
    setVehicleId('');
    setDriverId('');
    setReason('');
    setInventory(DEFAULT_INVENTORY);
    setPhotos({});
  };

  const handleSubmit = async () => {
    if (!auditType) return toast.error('Pick an audit type');
    if (!vehicleId) return toast.error('Pick a vehicle');
    if (!driverId) return toast.error('Pick a driver');
    if (!inventory.tool_kit_details) return toast.error('Tool Kit details (Yes/No) are required');

    setSubmitting(true);
    try {
      const payload = {
        audit_type: auditType,
        vehicle_id: vehicleId,
        driver_id: driverId,
        reason,
        ...inventory,
      };
      const { data } = await axios.post(`${API}/audit`, payload);
      toast.success(`Audit ${data.audit_id} submitted`);

      // Upload any photos
      const files = Object.entries(photos).filter(([, f]) => f);
      if (files.length) {
        setUploading(true);
        for (const [key, file] of files) {
          try {
            const b64 = await toBase64(file);
            await axios.post(`${API}/audit/${data.id}/upload`, {
              file_name: file.name,
              file_data: b64,
              document_type: key,
            });
          } catch (err) {
            toast.error(`${key} upload failed`);
          }
        }
        setUploading(false);
      }
      setLastSubmitted(data);
      resetForm();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="audit-link-invalid">
        <Card className="max-w-md w-full border-rose-200">
          <CardHeader className="text-center">
            <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto" />
            <CardTitle className="mt-2 text-rose-700">Link not available</CardTitle>
          </CardHeader>
          <CardContent className="text-center text-sm text-slate-600">
            This audit link has been revoked or does not exist. Please contact the admin for a fresh link.
          </CardContent>
        </Card>
      </div>
    );
  }

  const selectedVehicle = vehicles.find(v => v.id === vehicleId);
  const selectedDriver = drivers.find(d => d.id === driverId);

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-3" data-testid="audit-link-page">
      <Toaster richColors />
      <div className="max-w-3xl mx-auto space-y-5">
        {/* Header */}
        <div className="bg-white border rounded-xl p-4 flex items-center gap-3 shadow-sm">
          <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center">
            <Shield className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-slate-900">Field Audit</h1>
            <p className="text-xs text-slate-500">
              Signed in as <span className="font-semibold text-slate-700">{link?.name}</span> · token-based access
            </p>
          </div>
          <Badge className="bg-slate-900 text-white">Secure link</Badge>
        </div>

        {lastSubmitted && (
          <Card className="bg-emerald-50 border-emerald-200" data-testid="audit-success-banner">
            <CardContent className="p-3 flex items-center gap-2 text-emerald-800 text-sm">
              <CheckCircle2 className="w-4 h-4" />
              Last audit submitted: <span className="font-mono font-semibold">{lastSubmitted.audit_id}</span>
              <span className="opacity-70">({lastSubmitted.audit_type})</span>
            </CardContent>
          </Card>
        )}

        {/* Audit type picker */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">1 · Audit Type</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {(link?.allowed_types || []).map(t => {
              const meta = AUDIT_TYPE_META[t] || {};
              const Icon = meta.icon;
              const active = auditType === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setAuditType(t)}
                  className={`border rounded-lg p-3 text-left transition-all ${active ? 'ring-2 ring-slate-900 border-slate-900 bg-slate-900 text-white' : 'bg-white hover:border-slate-400'}`}
                  data-testid={`audit-type-${t.toLowerCase()}`}
                >
                  <div className="flex items-center gap-2">
                    {Icon && <Icon className="w-4 h-4" />}
                    <span className="font-semibold text-sm">{t}</span>
                  </div>
                </button>
              );
            })}
          </CardContent>
        </Card>

        {/* Vehicle picker */}
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base">2 · Vehicle</CardTitle>
            {selectedVehicle && (
              <Badge className="bg-slate-100 text-slate-700 border-slate-200 font-mono">
                {selectedVehicle.vehicle_number}
              </Badge>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              placeholder="Search by vehicle number / make / model"
              value={vehicleSearch}
              onChange={(e) => setVehicleSearch(e.target.value)}
              data-testid="vehicle-search"
            />
            <div className="max-h-60 overflow-y-auto border rounded-md divide-y">
              {filteredVehicles.length === 0 && (
                <div className="p-4 text-center text-sm text-slate-500">No vehicles match.</div>
              )}
              {filteredVehicles.map(v => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setVehicleId(v.id)}
                  className={`w-full text-left p-3 text-sm flex items-center justify-between hover:bg-slate-50 ${vehicleId === v.id ? 'bg-slate-100' : ''}`}
                  data-testid={`vehicle-row-${v.vehicle_number}`}
                >
                  <div>
                    <span className="font-mono font-semibold">{v.vehicle_number}</span>
                    <span className="text-slate-500 ml-2">{v.make} {v.model}</span>
                  </div>
                  {v.is_allotted && <Badge className="bg-blue-100 text-blue-700 border-blue-200">Allotted</Badge>}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Driver picker */}
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base">3 · Driver</CardTitle>
            {selectedDriver && (
              <Badge className="bg-slate-100 text-slate-700 border-slate-200">
                {selectedDriver.name}
              </Badge>
            )}
          </CardHeader>
          <CardContent>
            <Select value={driverId} onValueChange={setDriverId}>
              <SelectTrigger data-testid="driver-select"><SelectValue placeholder="Pick a driver" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {drivers.map(d => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name} · {d.driver_id} · {d.mobile}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Reason */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">4 · Reason / Notes</CardTitle></CardHeader>
          <CardContent>
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={auditType === 'Repairs' ? 'Describe the repair reason…' : 'Any notes…'}
              data-testid="audit-reason"
            />
          </CardContent>
        </Card>

        {/* Inventory checklist */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">5 · Inventory Checklist</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {['tyre1','tyre2','tyre3','tyre4','stepney','battery'].map(k => (
                <div key={k} className="border rounded-md p-3 space-y-2 bg-slate-50">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                    {k.startsWith('tyre') ? `Tyre ${k.slice(-1)}` : k[0].toUpperCase() + k.slice(1)}
                  </p>
                  <Input
                    placeholder="Brand"
                    value={inventory[`${k}_brand`]}
                    onChange={(e) => setInventory({ ...inventory, [`${k}_brand`]: e.target.value })}
                    data-testid={`inv-${k}-brand`}
                  />
                  <Input
                    placeholder="Number / serial"
                    value={inventory[`${k}_number`]}
                    onChange={(e) => setInventory({ ...inventory, [`${k}_number`]: e.target.value })}
                    data-testid={`inv-${k}-number`}
                  />
                </div>
              ))}
            </div>
            <div>
              <Label className="text-xs text-slate-500">Tool Kit Available? *</Label>
              <Select
                value={inventory.tool_kit_details}
                onValueChange={(v) => setInventory({ ...inventory, tool_kit_details: v })}
              >
                <SelectTrigger data-testid="inv-toolkit"><SelectValue placeholder="Yes / No" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Photos */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">6 · Photos (Optional)</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {PHOTO_FIELDS.map(({ key, label }) => (
              <div key={key} className="border rounded-md p-3 bg-slate-50 space-y-2">
                <Label className="text-xs text-slate-600">{label}</Label>
                <Input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => setPhotos({ ...photos, [key]: e.target.files?.[0] || null })}
                  data-testid={`photo-${key}`}
                />
                {photos[key] && <p className="text-xs text-emerald-700 truncate">✓ {photos[key].name}</p>}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="pb-10">
          <Button
            className="w-full h-12 text-base"
            onClick={handleSubmit}
            disabled={submitting || uploading}
            data-testid="submit-audit"
          >
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
              : uploading ? <><Upload className="w-4 h-4 mr-2 animate-pulse" /> Uploading photos…</>
              : 'Submit Audit'}
          </Button>
        </div>
      </div>
    </div>
  );
}
